<div class="details-step-container">
    <div class="dialog-title-section">
        <h2 class="dialog-title">{{ getSubStepTitle(subSteps[activeSubStepIndex]) }}</h2>
        <p class="dialog-subtitle">{{ 'permit.create.details.subtitle' | translate }}</p>
        <div class="substep-bar" *ngIf="subSteps.length > 1">
            <div class="substep-item" *ngFor="let subStep of subSteps; let i = index" [class.active]="i === activeSubStepIndex">
                <div class="substep-track"></div>
                <span class="substep-label">{{ getSubStepTitle(subStep) }}</span>
            </div>
        </div>
    </div>

    <!-- Dynamic form driven by the permit type's configured fields. Every content sub-step's fields
         stay mounted (never destroyed/recreated); only the active one is shown. Filtering by *ngFor
         instead would tear down and rebuild app-form-select-v2 on every sub-step switch, and it can
         lose its displayed (though still form-control-correct) value on remount. -->
    <form [formGroup]="form" *ngIf="form" class="dynamic-form">
        <div class="field-section" *ngFor="let subStep of subSteps; let i = index" [hidden]="subStep.isDocuments || i !== activeSubStepIndex">
            <ng-container *ngFor="let field of subStep.fields">
                <div class="field-row" [ngSwitch]="field.fieldType">
                    <app-form-input-v2 *ngSwitchCase="'text'" [label]="fieldLabel(field) + (field.required ? ' *' : '')" [subtitle]="fieldDescription(field)" placeholder=" " [formControlName]="field.fieldId">
                        <app-form-error *ngIf="form.get(field.fieldId).touched && form.get(field.fieldId).hasError('required')">
                            {{ 'permit.create.errors.required' | translate }}
                        </app-form-error>
                    </app-form-input-v2>

                    <app-form-input-v2
                        *ngSwitchCase="'number'"
                        [label]="fieldLabel(field) + (fieldUnit(field) ? ' (' + fieldUnit(field) + ')' : '') + (field.required ? ' *' : '')"
                        [subtitle]="fieldDescription(field)"
                        placeholder=" "
                        type="number"
                        [formControlName]="field.fieldId"
                    >
                        <app-form-error *ngIf="form.get(field.fieldId).touched && form.get(field.fieldId).hasError('required')">
                            {{ 'permit.create.errors.required' | translate }}
                        </app-form-error>
                        <app-form-error *ngIf="form.get(field.fieldId).touched && (form.get(field.fieldId).hasError('min') || form.get(field.fieldId).hasError('max'))">
                            {{ 'permit.create.errors.out_of_range' | translate: { min: field.min, max: field.max } }}
                        </app-form-error>
                    </app-form-input-v2>

                    <app-form-textarea *ngSwitchCase="'textarea'" [label]="fieldLabel(field) + (field.required ? ' *' : '')" [subtitle]="fieldDescription(field)" placeholder="" [rows]="4" [formControlName]="field.fieldId">
                        <app-form-error *ngIf="form.get(field.fieldId).touched && form.get(field.fieldId).hasError('required')">
                            {{ 'permit.create.errors.required' | translate }}
                        </app-form-error>
                    </app-form-textarea>

                    <app-form-datepicker *ngSwitchCase="'date'" [label]="fieldLabel(field) + (field.required ? ' *' : '')" [subtitle]="fieldDescription(field)" placeholder="" [formControlName]="field.fieldId">
                        <app-form-error *ngIf="form.get(field.fieldId).touched && form.get(field.fieldId).hasError('required')">
                            {{ 'permit.create.errors.required' | translate }}
                        </app-form-error>
                    </app-form-datepicker>

                    <app-form-select-v2
                        *ngSwitchCase="'dropdown'"
                        [label]="fieldLabel(field) + (field.required ? ' *' : '')"
                        [subtitle]="fieldDescription(field)"
                        placeholder=""
                        [formControlName]="field.fieldId"
                        [options]="dropdownOptions[field.fieldId]"
                        [invalid]="form.get(field.fieldId).touched && form.get(field.fieldId).invalid"
                    >
                        <app-form-error *ngIf="form.get(field.fieldId).touched && form.get(field.fieldId).hasError('required')">
                            {{ 'permit.create.errors.required' | translate }}
                        </app-form-error>
                    </app-form-select-v2>

                    <div *ngSwitchCase="'checkbox'" class="checkbox-field">
                        <app-form-toggle
                            [formControlName]="field.fieldId"
                            [ariaDescribedby]="fieldDescription(field) ? 'checkbox-description-' + field.fieldId : null"
                        >
                            {{ fieldLabel(field) }}{{ field.required ? ' *' : '' }}
                        </app-form-toggle>
                        <p class="helper-text" *ngIf="fieldDescription(field) as d" [id]="'checkbox-description-' + field.fieldId">{{ d }}</p>
                    </div>

                    <div *ngSwitchCase="'address'" class="address-field">
                        <label class="field-label">{{ fieldLabel(field) }}{{ field.required ? ' *' : '' }}</label>
                        <p class="helper-text" *ngIf="fieldDescription(field) as d">{{ d }}</p>
                        <input
                            type="text"
                            class="address-input"
                            [formControlName]="field.fieldId"
                            [matAutocomplete]="fieldAuto"
                            placeholder=""
                        />
                        <mat-autocomplete #fieldAuto="matAutocomplete" [displayWith]="displayAddressFn" (optionSelected)="onAddressPicked(field, $event.option.value)">
                            <mat-option *ngFor="let address of addressSuggestions[field.fieldId] | async" [value]="address">
                                {{ formatAddress(address) }}
                            </mat-option>
                        </mat-autocomplete>
                        <div class="field-error" *ngIf="form.get(field.fieldId).touched && form.get(field.fieldId).hasError('required')">
                            {{ 'permit.create.errors.required' | translate }}
                        </div>
                    </div>

                    <div *ngSwitchCase="'url'" class="url-field">
                        <span class="field-label">{{ fieldLabel(field) }}</span>
                        <p class="helper-text" *ngIf="fieldDescription(field) as d">{{ d }}</p>
                        <a class="field-link" [href]="field.presetValue" target="_blank" rel="noopener noreferrer">{{ field.presetValue }}</a>
                    </div>
                </div>
            </ng-container>
        </div>
    </form>

    <!-- `file` fields upload on pick so the form data carries a URL -->
    <div class="field-file-section" *ngIf="subSteps[activeSubStepIndex]?.isDocuments">
        <div class="field-file-row" *ngFor="let field of subSteps[activeSubStepIndex]?.fields">
            <label class="field-label">{{ fieldLabel(field) }}{{ field.required ? ' *' : '' }}</label>
            <p class="helper-text" *ngIf="fieldDescription(field) as d">{{ d }}</p>

            <div class="field-file-list" *ngIf="fileUrlsFor(field).length">
                <div class="field-file-body" *ngFor="let url of fileUrlsFor(field); let i = index">
                    <mat-icon class="file-icon">attach_file</mat-icon>
                    <a class="field-file-name" [href]="url" target="_blank" rel="noopener noreferrer">{{ fileNameAt(field, i) }}</a>
                    <button type="button" class="field-file-remove" [attr.aria-label]="'permit.create.details.remove_file' | translate: { name: fileNameAt(field, i) }" (click)="removeFieldFile(field, i)">
                        <i class="icon-close"></i>
                    </button>
                </div>
            </div>
            <label class="field-file-picker" [class.disabled]="isFileFieldFull(field)">
                <input type="file" class="hidden-file-input" (change)="onFieldFileSelected(field, $event)" [disabled]="fileFieldUploading[field.fieldId] || isFileFieldFull(field)" />
                <mat-spinner *ngIf="fileFieldUploading[field.fieldId]" diameter="18"></mat-spinner>
                <span *ngIf="!fileFieldUploading[field.fieldId]">{{ 'permit.create.details.choose_file' | translate }}</span>
            </label>
        </div>
    </div>

    <div class="empty-fields" *ngIf="!hasCustomFields">
        {{ 'permit.create.details.no_custom_fields' | translate }}
    </div>
</div>
